import type { ErrorCode } from '@/bindings/ErrorCode'

/** Placeholders are `{name}`, filled from the `params` passed to `t`. */
const errors: Record<`error.${ErrorCode}`, string> = {
  'error.NOT_FOUND': 'Not found.',
  'error.METHOD_NOT_ALLOWED': 'That action is not allowed here.',
  'error.RATE_LIMITED': 'Too many attempts. Try again in {wait}.',
  'error.BAD_REQUEST': 'The request could not be understood.',
  'error.UNAUTHORIZED': 'Please log in to continue.',
  'error.INVALID_CREDENTIALS': 'Wrong login or password.',
  'error.CSRF_MISMATCH': 'Your session expired. Please try again.',
  'error.INVALID_PHONE': 'Enter a valid phone number.',
  'error.INVALID_EMAIL': 'Enter a valid email address.',
  'error.PASSWORD_TOO_SHORT': 'Password must be at least {min} characters (you entered {length}).',
  'error.PASSWORD_TOO_LONG': 'Password must be at most {max} characters.',
  'error.PASSWORD_TOO_COMMON': 'That password is too common. Choose another.',
  'error.VERIFICATION_INVALID': 'That code is not valid.',
  'error.VERIFICATION_EXPIRED': 'That code has expired. Request a new one.',
  'error.IDENTIFIER_ALREADY_HELD': 'This account already uses it.',
  'error.LAST_IDENTIFIER': 'You cannot remove your only login method.',
  'error.DELETE_BLOCKED': 'This account cannot be deleted right now: {reason}.',
  'error.SLUG_RESERVED': 'This URL name is reserved. Choose another.',
  'error.SLUG_TAKEN': 'This URL name is already taken. Choose another.',
  'error.ACCOUNT_NOT_FOUND': 'No account matches that phone number or email.',
  'error.FORBIDDEN': 'You do not have permission to do that.',
  'error.EMAIL_TAKEN': 'Another account already uses this email.',
  'error.LAST_ADMIN': 'The last platform admin cannot be demoted.',
  'error.ROLE_NOT_MANAGEABLE': 'You can only manage roles below your own.',
  'error.BRANCH_REQUIRED': 'Choose at least one branch for this role.',
  'error.IMAGE_TOO_LARGE': 'The image is too large. Pick one under {max_mb} MB.',
  'error.INVALID_IMAGE': 'That file is not a usable image. Use a JPEG, PNG, or WebP photo.',
  'error.MISSING_MAIN_LANGUAGE': 'Fill in the text in the restaurant’s main language.',
  'error.INVALID_PRICE': 'Enter a price from 0 to {max} with at most {max_decimals} decimals.',
  'error.CATEGORY_NOT_EMPTY': 'Move or delete the {items} items in this category first.',
  'error.IMAGE_NOT_FOUND': 'That picture is no longer available. Upload it again.',
  'error.INVALID_STOCK': 'Enter a whole number from 0 to {max}.',
  'error.MENU_PRICES_NOT_REPRESENTABLE':
    '{items} menu price(s) are not exact in {currency}. Fix those prices first, or pick another currency.',
  'error.ORDERING_CLOSED': 'This branch is not taking orders right now.',
  'error.ORDER_PIN_INVALID': 'The PIN is missing or wrong. Ask the staff for it.',
  'error.ACTIVE_ORDER_CAP': 'You already have {cap} active order(s) here. Wait until one is done.',
  'error.TABLE_NOT_FOUND': 'This table’s QR code is no longer valid. Ask the staff for a new one.',
  'error.ITEM_UNAVAILABLE': 'An item in your cart is no longer available. Remove it and try again.',
  'error.INSUFFICIENT_STOCK': 'Not enough of an item in your cart is left. Reduce its quantity and try again.',
  'error.INVALID_QUANTITY': 'Quantities must be a multiple of {step}, up to {max}.',
  'error.LINE_NOTE_TOO_LONG': 'A note for one dish is over {max} characters. Shorten it and try again.',
  'error.SESSION_CLOSED': 'This table’s session was just closed. Scan the QR code again.',
  'error.IDEMPOTENCY_KEY_TAKEN': 'This order was already sent from somewhere else. Reload and try again.',
  'error.ORDER_NOT_CANCELABLE': 'This order can no longer be canceled.',
  'error.ORDER_MOVED': 'This order has already moved on.',
  'error.SESSION_HAS_ACTIVE_ORDERS': 'This session still has {count} active orders.',
  'error.TABLE_BUSY': 'Table {table_name} already has an open session.',
  'error.INVALID_RETURN': 'Enter a multiple of {step}, up to {remaining}.',
  'error.INVALID_REAL_WEIGHT': 'Enter a weight from {min} to {max}, in steps of {step}.',
  'error.INVALID_TABLE_NAME': 'Enter a table name of 1–{max} characters.',
  'error.TABLE_NAME_TAKEN': 'Another table in this branch already has this name.',
  'error.TABLE_IN_USE': 'This table has an open session. Close it before deleting the table.',
  'error.INVALID_EXPIRY_MINUTES': 'Enter 1–{max} minutes.',
  'error.INVALID_ACTIVE_ORDER_CAP': 'Enter 1–{max} orders.',
  'error.INVALID_ORDER_PIN': 'Enter a PIN of 1–{max} characters.',
  'error.FEEDBACK_ALREADY_SENT': 'This order already has feedback.',
  'error.FEEDBACK_DAILY_CAP': 'You have sent {max} messages in the last day. Try again in {wait}.',
  'error.INVALID_FEEDBACK_MESSAGE': 'Write between 1 and {max} characters.',
  'error.RESTAURANT_DEACTIVATED': 'This restaurant is deactivated. Contact Romi.',
  'error.BRANCH_CLOSED': 'This branch is deactivated.',
  'error.RESTAURANT_CAP_REACHED': 'Your account already has a restaurant. Contact Romi to add another.',
  'error.RESTAURANT_ACTIVE': 'The restaurant is still active. Deactivate it first.',
  'error.RESTAURANT_ON_HOLD': 'The restaurant is on hold until {until}.',
  'error.SLUG_MISMATCH': 'The URL name does not match this restaurant.',
  'error.INTERNAL': 'Something went wrong. Please try again.',
}

/** `DELETE_BLOCKED.reason` values; new reasons from E1/G1 land here. */
const deleteReasons = {
  'deleteReason.last_admin': 'it is the last platform admin',
  'deleteReason.superuser': 'it owns a restaurant',
  'deleteReason.active_orders': 'it has active orders',
  'deleteReason.open_session': 'it has an open table session',
  'deleteReason.self': 'it is your own account',
}

/** Retry-after waits; `{n}` is already rounded up to the unit. */
const durations = {
  'duration.seconds': '{n} second(s)',
  'duration.minutes': '{n} minute(s)',
  'duration.hours': '{n} hour(s)',
}

export const en = {
  ...errors,
  ...deleteReasons,
  ...durations,
  'app.name': 'Romi',
  'app.platform': 'Romi platform',
  'notFound.title': 'Page not found',
  'notFound.home': 'Go to the home page',
  'theme.label': 'Theme',
  'theme.system': 'System',
  'theme.light': 'Light',
  'theme.dark': 'Dark',
  'language.label': 'Language',
  'nav.label': 'Main navigation',
  'nav.restaurants': 'Restaurants',
  'nav.users': 'Platform users',
  'nav.otp': 'OTP requests',
  'nav.systemLog': 'System log',
  'nav.cleanup': 'Data cleanup',
  'nav.myRestaurants': 'My restaurants',
  'nav.settings': 'Settings',
  'nav.accounts': 'Accounts',
  'nav.menu': 'Menu',
  'nav.branches': 'Branches',
  'nav.manageRestaurant': 'Manage restaurant',
  'nav.branchMenu': 'Branch menu',
  'nav.tables': 'Tables',
  'nav.branchSettings': 'Branch settings',
  'nav.branchInfo': 'Branch info',
  'nav.staff': 'Staff',
  'nav.queue': 'Queue',
  'nav.sessions': 'Sessions',
  'nav.createOrder': 'Create order',
  'nav.search': 'Search',
  'nav.statistics': 'Statistics',
  'nav.more': 'More',
  'nav.account': 'Account menu',
  'nav.collapse': 'Collapse sidebar',
  'nav.expand': 'Expand sidebar',
  'nav.group.operation': 'Operation',
  'nav.group.branch': 'Branch management',
  'nav.group.restaurant': 'Restaurant management',
  'nav.group.platform': 'Platform',
  'auth.login': 'Log in',
  'auth.logout': 'Log out',
  'auth.platformLogin': 'Platform login',
  'auth.identifierToggle': 'Phone or email',
  'auth.phone': 'Phone',
  'auth.email': 'Email',
  'auth.nation': 'Country',
  'auth.phoneNumber': 'Phone number',
  'auth.password': 'Password',
  'auth.showPassword': 'Show password',
  'auth.register': 'Register',
  'auth.registerIntro': 'Enter your phone number or email and we will send you a verification code.',
  'auth.sendCode': 'Send code',
  'auth.haveCode': 'I already have a code',
  'auth.verifyTitle': 'Verify and set your password',
  'auth.code': 'Verification code',
  'auth.codeSentEmail': 'Enter the code we emailed you. A code lasts 10 minutes; requesting a new one replaces it.',
  'auth.codeSentPhone':
    'An operator will text you your code — this can take a while, and there is nothing to enter until it arrives. You do not need to request again.',
  'auth.codeSentToPhone': 'Code sent to your phone.',
  'auth.codeSentToEmail': 'Code sent to your email.',
  'auth.resetCodeSentToPhone': 'If it matches an account, a code has been sent to your phone.',
  'auth.resetCodeSentToEmail': 'If it matches an account, a code has been sent to your email.',
  'auth.expiresIn': 'Expires in {time}.',
  'auth.codeExpired': 'That code has expired.',
  'auth.didntGetIt': "Didn't get it?",
  'auth.useEmailInstead': 'Use email instead',
  'auth.smsLimitNotice':
    'SMS limit reached for this number today. An operator can text you a code by hand — this can take a while.',
  'auth.askOperator': 'Ask an operator to text you a code',
  'auth.noMoreCodes': 'No more codes for this identifier today.',
  'auth.smsUnsupportedHint':
    'SMS is not available for this country yet. For an instant code, use your email or a Vietnamese phone number.',
  'auth.firstName': 'First name',
  'auth.lastName': 'Last name',
  'auth.resendCode': 'Send a new code',
  'auth.codeRequested': 'A new code has been requested.',
  'auth.createAccount': 'Create account',
  'auth.forgotPassword': 'Forgot password',
  'auth.forgotIntro': 'Enter your phone number or email. If it matches an account, we will send you a reset code.',
  'auth.forgotIntroEmail': 'Enter your email. If it matches an account, we will send you a reset code.',
  'auth.resetTitle': 'Reset your password',
  'auth.resetCodeSentEmail':
    'If it matches an account, a code has been emailed to you. A code lasts 10 minutes; requesting a new one replaces it.',
  'auth.resetCodeSentPhone':
    'If it matches an account, an operator will text you a code — this can take a while, and there is nothing to enter until it arrives. You do not need to request again.',
  'auth.newPassword': 'New password',
  'auth.currentPassword': 'Current password',
  'auth.resetPassword': 'Reset password',
  'auth.passwordRuleLength': 'At least {min} characters',
  'auth.passwordRuleCommon': 'Not a commonly used password',
  'auth.passwordRuleMax': 'At most {max} characters',
  'profile.title': 'Profile',
  'profile.detailsTitle': 'Name & language',
  'profile.saved': 'Saved.',
  'profile.passwordTitle': 'Change password',
  'profile.passwordChanged': 'Password changed. Your other devices have been logged out.',
  'profile.loginTitle': 'Login methods',
  'profile.notSet': 'Not set',
  'profile.add': 'Add',
  'profile.change': 'Change',
  'profile.remove': 'Remove',
  'profile.reenterHint': 'Enter your current password to confirm.',
  'profile.pendingIdentifier': 'Verifying {identifier}.',
  'profile.verify': 'Verify',
  'profile.deleteTitle': 'Delete account',
  'profile.deleteIntro':
    'Deletes your account and removes your personal data. This cannot be undone.',
  'profile.deleteConfirm': 'Delete my account',
  'slug.empty': 'Enter a URL name.',
  'slug.tooLong': 'At most {max} characters.',
  'slug.forbiddenChar': 'Only lowercase letters, digits and hyphens.',
  'slug.edgeHyphen': 'Cannot start or end with a hyphen.',
  'slug.doubleHyphen': 'Cannot contain two hyphens in a row.',
  'slug.checking': 'Checking availability…',
  'slug.available': 'Available.',
  'restaurants.title': 'Restaurants',
  'restaurants.create': 'Create restaurant',
  'restaurants.empty': 'No restaurants yet.',
  'restaurants.pager': 'Restaurant pages',
  'restaurants.branchCount': '{count} branch(es)',
  'restaurants.superuserIs': 'Superuser: {name}',
  'restaurants.active': 'Active',
  'restaurants.deactivated': 'Deactivated',
  'restaurants.noSuperuser': 'No superuser',
  'restaurants.noSuperuserHint': 'Nobody can manage this restaurant until an owner account is linked.',
  'restaurants.restaurantSection': 'Restaurant',
  'restaurants.name': 'Name',
  'restaurants.slug': 'URL name',
  'restaurants.slugHint': 'Lowercase letters, digits and hyphens. This is the restaurant’s web address and cannot be changed later.',
  'restaurants.color': 'Color',
  'restaurants.mainLanguage': 'Main language',
  'restaurants.secondaryLanguages': 'Secondary languages',
  'restaurants.createdAt': 'Created',
  'restaurants.firstBranch': 'First branch',
  'restaurants.branches': 'Branches',
  'restaurants.branchName': 'Branch name',
  'restaurants.branchSlug': 'Branch URL name',
  'restaurants.branchSlugHint': 'Unique within the restaurant; part of the branch’s web address.',
  'restaurants.currencyHint': 'Follows the time zone until you pick one.',
  'restaurants.timezone': 'Time zone',
  'restaurants.currency': 'Currency',
  'restaurants.addBranch': 'Add branch',
  'restaurants.branchAdded': 'Branch {name} added.',
  'restaurants.superuser': 'Superuser',
  'restaurants.orders': 'Orders',
  'restaurants.superuserHint': 'The exact phone number or email of the owner’s registered Romi account.',
  'restaurants.accountFound': 'Account found',
  'restaurants.searchSuperuserFirst': 'Search for the superuser account first, or clear the field.',
  'restaurants.linkSuperuser': 'Link superuser',
  'restaurants.changeSuperuser': 'Change superuser',
  'restaurants.superuserChanged': 'Superuser updated.',
  'restaurants.deactivate': 'Deactivate',
  'restaurants.reactivate': 'Reactivate',
  'restaurants.confirmDeactivate': 'Confirm deactivation',
  'restaurants.confirmDeactivateOf': 'Confirm deactivation of {name}',
  'restaurants.notices': 'Notices',
  'restaurants.noticeHint': 'A templated notice to every manager and superuser. Deactivated and reactivated are sent by the buttons above.',
  'restaurants.noticeTemplate': 'Template',
  'restaurants.noticeAmount': 'Amount',
  'restaurants.noticeAmountHint': 'As text, e.g. 300,000 VND.',
  'restaurants.noticeDueDate': 'Payment due',
  'restaurants.noticeDeactivateOn': 'Deactivate on',
  'restaurants.noticeRemoveOn': 'Remove on',
  'restaurants.sendNotice': 'Send notice',
  'restaurants.confirmSend': 'Confirm send',
  'restaurants.noticeGoesTo': 'Every manager and superuser of {name} sees this in the app.',
  'restaurants.noticeGoesToEmail': 'Every manager and superuser of {name} sees this in the app and gets it by email.',
  'restaurants.noticeSent': 'Notice sent to {recipients} account(s), {emails} by email.',
  'restaurants.filter': 'Show',
  'restaurants.filterAll': 'All',
  'restaurants.filterDeletable': 'Deletion candidates',
  'restaurants.emptyDeletable': 'No deletion candidates: every deactivated restaurant is on hold, or none is deactivated.',
  'restaurants.lastOrder': 'Last order {date}',
  'restaurants.noOrders': 'No orders yet',
  'restaurants.deactivatedOn': 'Deactivated {date}',
  'restaurants.hold': 'Hold',
  'restaurants.holdHint': 'Keeps the restaurant out of the deletion candidates until this date has passed. Note who asked and why.',
  'restaurants.heldUntil': 'Held until {date}',
  'restaurants.noHold': 'No hold',
  'restaurants.holdUntil': 'Keep until',
  'restaurants.holdNote': 'Note',
  'restaurants.saveHold': 'Save hold',
  'restaurants.clearHold': 'Clear hold',
  'restaurants.holdSaved': 'Hold saved.',
  'restaurants.holdCleared': 'Hold cleared.',
  'restaurants.danger': 'Delete restaurant',
  'restaurants.deleteHint': 'Removes the restaurant and everything in it: branches, menu, pictures, tables, orders, history. Accounts stay. This cannot be undone.',
  'restaurants.deleteNeedsDeactivation': 'Only a deactivated restaurant can be deleted. Deactivate it first.',
  'restaurants.deleteHeld': 'On hold until {date}.',
  'restaurants.deleteHeldNote': 'On hold until {date}: {note}',
  'restaurants.checkDelete': 'Check what would be deleted',
  'restaurants.deleteSlugLabel': 'URL name',
  'restaurants.deleteTypeSlug': 'Type {slug} to confirm.',
  'restaurants.delete': 'Delete restaurant',
  'restaurants.confirmDelete': 'Yes, delete',
  'restaurants.confirmDeleteOf': 'Confirm deletion of {name}',
  'restaurants.confirmDeleteText': '{rows} rows go and cannot be recovered.',
  'notice.template.payment_reminder': 'Payment reminder',
  'notice.template.deactivation_warning': 'Deactivation warning',
  'notice.template.deactivated': 'Deactivated',
  'notice.template.payment_received': 'Payment received',
  'notice.template.reactivated': 'Reactivated',
  'notice.template.removal_notice': 'Removal notice',
  'users.title': 'Platform users',
  'users.create': 'Create user',
  'users.createIntro':
    'No password is set here. The person sets their own through Forgot password on the platform login page.',
  'users.empty': 'No platform users yet.',
  'users.pager': 'Platform user pages',
  'users.admin': 'Admin',
  'users.you': 'You',
  'users.isAdmin': 'Platform admin',
  'users.isAdminHint': 'Admins can manage platform users. Everyone else manages restaurants and OTP requests.',
  'users.createdAt': 'Created',
  'users.created': 'Account created for {email}.',
  'users.firstLoginTitle': 'First login',
  'users.firstLoginIntro':
    'No email was sent. Ask {email} to open the page below and use Forgot password to set their own password. The same page recovers a lost password.',
  'users.openUser': 'Open user',
  'users.createAnother': 'Create another',
  'users.deleteTitle': 'Delete account',
  'users.deleteIntro': 'Removes this person’s access and personal data. This cannot be undone.',
  'users.deleteSelfHint': 'To delete your own account, use your profile page.',
  'users.delete': 'Delete',
  'users.confirmDelete': 'Confirm deletion',
  'users.confirmDeleteOf': 'Confirm deletion of {name}',
  'users.adminOnly': 'Only platform admins can manage platform users.',
  'otp.title': 'OTP requests',
  'otp.tabs': 'OTP request views',
  'otp.pending': 'Pending',
  'otp.sent': 'Sent',
  'otp.emptyPending': 'No pending requests.',
  'otp.emptySent': 'No codes sent yet.',
  'otp.pager': 'OTP request pages',
  'otp.purpose.verify_phone': 'Registration',
  'otp.purpose.reset_password': 'Password reset',
  'otp.requestedAt': 'Requested {time}',
  'otp.generate': 'Generate code',
  'otp.generateFor': 'Generate code for {identifier}',
  'otp.codeTitle': 'Code for {identifier}',
  'otp.codeIntro': 'Text this code to the number. It is shown only once here and stays valid until {expires}.',
  'otp.copy': 'Copy code',
  'otp.copied': 'Copied',
  'otp.done': 'Done',
  'otp.gone': 'That request is no longer pending — another operator may have handled it, or the person requested again.',
  'otp.sentBy': 'Sent by {name}, {time}',
  'otp.sentAt': 'Sent {time}',
  'otp.state.used': 'Used',
  'otp.state.expired': 'Expired',
  'otp.state.active': 'Active',
  'systemLog.title': 'System log',
  'systemLog.intro': 'Warnings and errors the server logged, newest first.',
  'systemLog.tabs': 'System log views',
  'systemLog.errors': 'Errors',
  'systemLog.all': 'All',
  'systemLog.emptyErrors': 'No errors logged.',
  'systemLog.emptyAll': 'Nothing logged.',
  'systemLog.pager': 'System log pages',
  'systemLog.level.error': 'Error',
  'systemLog.level.warn': 'Warning',
  'systemLog.details': 'Details',
  'cleanup.title': 'Data cleanup',
  'cleanup.intro':
    'Old rows accumulate across every restaurant. Pick a date per group, check what would be removed, then clean it up. Nothing is deleted without a check first.',
  'cleanup.vacuumNote':
    'Sizes are estimates. Postgres reuses the freed space for new rows rather than shrinking the disk, so the number tells what stops growing.',
  'cleanup.before': 'Remove rows older than',
  'cleanup.check': 'Check',
  'cleanup.run': 'Clean up',
  'cleanup.confirm': 'Yes, clean up',
  'cleanup.confirmText': 'Delete {rows} rows older than {before}? This cannot be undone.',
  'cleanup.progress': 'Deleted {rows} rows so far…',
  'cleanup.finished': 'Cleanup finished.',
  'cleanup.nothing': 'Nothing to clean up before that date.',
  'cleanup.mustBePast': 'The date must be in the past.',
  'cleanup.adminOnly': 'Only platform admins can clean up data.',
  'cleanup.col.table': 'Table',
  'cleanup.col.rows': 'Rows',
  'cleanup.col.bytes': 'Approx. size',
  'cleanup.group.orders': 'Orders',
  'cleanup.group.system_log': 'System log',
  'cleanup.group.notifications': 'Notifications',
  'cleanup.group.verification_codes': 'Verification codes',
  'cleanup.group.audit_log': 'Audit log',
  'cleanup.group.feedback': 'Feedback',
  'cleanup.describe.orders':
    'Completed, canceled and expired orders with their lines, closed dining sessions nothing references any more, and daily order counters. Live orders and open sessions always stay.',
  'cleanup.describe.system_log': 'Every warning and error entry older than the date.',
  'cleanup.describe.notifications': 'Read notifications older than the date. Unread ones stay.',
  'cleanup.describe.verification_codes':
    'Used, expired and locked-out codes older than the date. Requests still waiting for a manual code stay.',
  'cleanup.describe.audit_log': 'Every audit row older than the date. Restaurants lose the change history before it.',
  'cleanup.describe.feedback': 'Every feedback message older than the date, to restaurants and to Romi alike.',
  'cleanup.ordersWarning': 'Statistics and customer order history before this date disappear with the orders.',
  'cleanup.table.orders': 'Orders',
  'cleanup.table.order_lines': 'Order lines',
  'cleanup.table.dining_sessions': 'Dining sessions',
  'cleanup.table.order_counters': 'Order counters',
  'cleanup.table.system_log': 'System log entries',
  'cleanup.table.notifications': 'Notifications',
  'cleanup.table.verification_codes': 'Verification codes',
  'cleanup.table.audit_log': 'Audit rows',
  'cleanup.table.feedback': 'Feedback messages',
  'cleanup.table.branches': 'Branches',
  'cleanup.table.restaurant_roles': 'Roles',
  'cleanup.table.role_branches': 'Branch assignments',
  'cleanup.table.menu_categories': 'Menu categories',
  'cleanup.table.menu_items': 'Menu items',
  'cleanup.table.branch_menu_items': 'Branch menu overrides',
  'cleanup.table.dining_tables': 'Tables',
  'cleanup.table.images': 'Pictures',
  'cleanup.table.restaurants': 'Restaurant',
  'nav.auditLog': 'Audit log',
  'nav.feedback': 'Feedback',
  'audit.title': 'Audit log',
  'audit.intro': 'Who changed what, and from what to what. Newest first.',
  'audit.managerOnly': 'Only managers and the superuser can read the audit log.',
  'audit.empty': 'No changes recorded.',
  'audit.pager': 'Audit log pages',
  'audit.filter.branch': 'Branch',
  'audit.filter.allBranches': 'All branches',
  'audit.filter.entity': 'What changed',
  'audit.filter.allEntities': 'Everything',
  'audit.filter.from': 'From',
  'audit.filter.to': 'To',
  'audit.entity.restaurant': 'Restaurant',
  'audit.entity.branch': 'Branch',
  'audit.entity.menu_category': 'Menu category',
  'audit.entity.menu_item': 'Menu item',
  'audit.entity.table': 'Table',
  'audit.entity.account': 'Account',
  'audit.entity.order': 'Order',
  'audit.entity.session': 'Session',
  'audit.entity.platform_user': 'Platform user',
  'audit.entity.cleanup': 'Data cleanup',
  'audit.changes': 'Changes',
  'audit.col.field': 'Field',
  'audit.col.from': 'Before',
  'audit.col.to': 'After',
  'audit.actor.platform': 'Platform',
  'audit.actor.admin': 'Admin',
  'audit.actor.customer': 'Customer',
  'audit.actor.unknown': 'Unknown account',
  'audit.none': 'none',
  'audit.yes': 'yes',
  'audit.no': 'no',
  'audit.pin.set': 'set',
  'audit.pin.unset': 'not set',
  'audit.pin.changed': 'changed',
  'audit.mode.open': 'Open',
  'audit.mode.login': 'Login',
  'audit.mode.closed': 'Closed',
  'audit.unknown': 'Change: {key}',
  'audit.history.title': 'History',
  'audit.history.empty': 'No changes yet.',
  'audit.platform.title': 'Platform audit log',
  'audit.platform.intro': 'What platform users did to restaurants and accounts. Newest first.',
  'audit.platform.adminOnly': 'Only platform admins can read the platform audit log.',
  'audit.platform.filter.restaurant': 'Restaurant',
  'audit.platform.allRestaurants': 'All restaurants',
  'feedback.title': 'Feedback',
  'feedback.intro': 'What customers wrote about this restaurant, from their orders and the landing pages. Newest first.',
  'feedback.managerOnly': 'Only managers and the superuser can read feedback.',
  'feedback.empty': 'No feedback yet.',
  'feedback.pager': 'Feedback pages',
  'feedback.filter.branch': 'Branch',
  'feedback.filter.allBranches': 'All branches',
  'feedback.platform.title': 'Feedback about Romi',
  'feedback.platform.intro': 'What restaurant users want fixed or added. Newest first.',
  'feedback.author.anonymous': 'Anonymous customer',
  'feedback.author.deleted': 'Deleted account',
  'feedback.author.unnamed': 'Unnamed account',
  'feedback.about.order': 'Order #{number}',
  'feedback.about.branch': 'Branch {name}',
  'feedback.about.restaurant': 'The restaurant',
  'feedback.about.app': 'About Romi',
  'feedback.about.page': 'From {path}',
  'feedback.form.label': 'Your message',
  'feedback.form.count': '{count} / {max}',
  'feedback.form.send': 'Send feedback',
  'feedback.form.sentAt': 'Sent {time}',
  'feedback.form.thanks': 'Thanks, your feedback was sent.',
  'feedback.order.title': 'Feedback for this order',
  'feedback.order.hint': 'Tell {restaurant} how it went. One message per order.',
  'feedback.for': 'Feedback for {name}',
  'feedback.forRomi': 'Feedback for Romi',
  'audit.field.name': 'Name',
  'audit.field.color': 'Color',
  'audit.field.intro': 'Introduction',
  'audit.field.main_language': 'Main language',
  'audit.field.secondary_languages': 'Secondary languages',
  'audit.field.address': 'Address',
  'audit.field.phone': 'Phone',
  'audit.field.opening_hours': 'Opening hours',
  'audit.field.announcement': 'Announcement',
  'audit.field.ordering_mode': 'Ordering mode',
  'audit.field.open_order_expiry_minutes': 'Order expiry (minutes)',
  'audit.field.active_order_cap': 'Active orders per customer',
  'audit.field.order_pin': 'Order PIN',
  'audit.field.delivery_address_enabled': 'Delivery address',
  'audit.field.category_id': 'Category',
  'audit.field.price': 'Price',
  'audit.field.step': 'Quantity step',
  'audit.field.image_id': 'Image',
  'audit.field.available': 'Available',
  'audit.field.stock': 'Daily stock',
  'audit.field.branches': 'Branches',
  'audit.field.role': 'Role',
  'audit.field.returned_quantity': 'Returned',
  'audit.field.quantity': 'Quantity',
  'audit.field.line_total': 'Line total',
  'audit.field.table_name': 'Table',
  'audit.field.email': 'Email',
  'audit.field.first_name': 'First name',
  'audit.field.last_name': 'Last name',
  'audit.field.is_admin': 'Admin',
  'audit.field.hold_until': 'Keep until',
  'audit.field.hold_note': 'Hold note',
  'audit.action.restaurant.settings_updated': 'Updated the restaurant settings',
  'audit.action.branch.info_updated': 'Updated the info of branch {branch}',
  'audit.action.branch.settings_updated': 'Updated the settings of branch {branch}',
  'audit.action.branch.opened': 'Opened branch {branch}',
  'audit.action.branch.closed': 'Closed branch {branch}',
  'audit.action.menu.category_created': 'Added category {name}',
  'audit.action.menu.category_updated': 'Updated category {name}',
  'audit.action.menu.category_deleted': 'Deleted category {name}',
  'audit.action.menu.categories_reordered': 'Reordered the categories: {names}',
  'audit.action.menu.item_created': 'Added item {name} at {price}',
  'audit.action.menu.item_updated': 'Updated item {name}',
  'audit.action.menu.item_deleted': 'Deleted item {name}',
  'audit.action.branch_menu.availability_set': 'Changed the availability of {name} at branch {branch}',
  'audit.action.branch_menu.price_set': 'Changed the price of {name} at branch {branch}',
  'audit.action.role.granted': 'Granted {account} the {role} role',
  'audit.action.role.changed': 'Changed the role of {account} to {role}',
  'audit.action.role.branches_changed': 'Changed the branches of {account}',
  'audit.action.role.revoked': 'Revoked the {role} role from {account}',
  'audit.action.table.created': 'Added table {table}',
  'audit.action.table.renamed': 'Renamed table {table}',
  'audit.action.table.deleted': 'Deleted table {table}',
  'audit.action.order.status_changed': 'Moved order #{order} from {from} to {to}',
  'audit.action.order.line_returned': 'Returned {name} on order #{order}',
  'audit.action.order.real_weight_set': 'Set the real weight of {name} on order #{order}',
  'audit.action.session.closed': 'Closed the session at table {table}',
  'audit.action.session.table_changed': 'Moved session {code} to another table',
  'audit.action.session.merged': 'Merged the session at table {fromTable} into the one at table {table}',
  'audit.action.restaurant.created': 'Created restaurant {name} with branch {branch}',
  'audit.action.restaurant.deactivated': 'Deactivated restaurant {name}',
  'audit.action.restaurant.reactivated': 'Reactivated restaurant {name}',
  'audit.action.restaurant.superuser_changed': 'Made {account} the superuser',
  'audit.action.restaurant.superuser_replaced': 'Made {account} the superuser, replacing {from}',
  'audit.action.branch.created': 'Added branch {branch}',
  'audit.action.branch.deactivated': 'Deactivated branch {branch} (platform)',
  'audit.action.branch.reactivated': 'Reactivated branch {branch} (platform)',
  'audit.action.branch.closure': 'Deactivated branch {branch}',
  'audit.action.branch.reopening': 'Reactivated branch {branch}',
  'audit.action.restaurant.notice_sent': 'Sent the notice “{template}” to {name}, {recipients} recipient(s)',
  'audit.action.restaurant.hold_set': 'Set a hold on {name}',
  'audit.action.restaurant.hold_cleared': 'Cleared the hold on {name}',
  'audit.action.restaurant.deleted': 'Deleted restaurant {name} (/{slug}): {rows}',
  'audit.action.platform_user.created': 'Created platform user {email}',
  'audit.action.platform_user.updated': 'Updated platform user {email}',
  'audit.action.platform_user.deleted': 'Deleted platform user {email}',
  'audit.action.cleanup.ran': 'Cleaned up {group} older than {before}: {rows}',
  'notification.title': 'Notifications',
  'notification.bell': 'Notifications',
  'notification.bellUnread': 'Notifications, {count} unread',
  'notification.empty': 'No notifications yet.',
  'notification.pager': 'Notification pages',
  'notification.seeAll': 'See all',
  'notification.markAllRead': 'Mark all read',
  'notification.new': 'New',
  'notification.identifier_claimed':
    'Your {channel} {identifier} was verified by another account and removed from yours. You can still log in with your other login method.',
  'notification.otp_requested': 'A new OTP request ({purpose}) is waiting in the queue.',
  'notification.role_granted': 'You are now {role} at {restaurant}.',
  'notification.role_revoked': 'Your {role} role at {restaurant} was removed.',
  'notification.feedback.restaurant': 'New feedback for {restaurant}.',
  'notification.feedback.app': 'New feedback about Romi.',
  'notification.restaurant_created': '{creator} created the restaurant {restaurant}.',
  'notification.someone': 'A restaurant user',
  'notification.notice.payment_reminder': 'Payment reminder for {restaurant}: {amount} is due by {dueDate}.',
  'notification.notice.deactivation_warning': '{restaurant} will be deactivated on {deactivateOn} unless payment is received by {dueDate}.',
  'notification.notice.deactivated': '{restaurant} has been deactivated. Contact Romi to restore it.',
  'notification.notice.payment_received': 'Payment of {amount} for {restaurant} received. Thank you.',
  'notification.notice.reactivated': '{restaurant} is active again.',
  'notification.notice.removal_notice': 'The data of {restaurant} will be removed on {removeOn} unless you contact Romi.',
  'notification.restaurant_removed': 'The restaurant {restaurant} and its data have been removed.',
  'notification.unknown': 'Notification: {key}',
  'role.staff': 'Staff',
  'role.admin': 'Admin',
  'role.manager': 'Manager',
  'role.superuser': 'Superuser',
  'backOffice.title': 'My restaurants',
  'backOffice.branchNotInScope': 'This branch is not one you work in.',
  'backOffice.restaurantDeactivated': 'This restaurant is deactivated. Contact Romi to reactivate it.',
  'backOffice.branchClosed': 'This branch is deactivated. Only its statistics remain available.',
  'backOffice.empty': 'You have no role in any restaurant yet. Ask your restaurant to grant you one.',
  'backOffice.emptyCreate': 'Own a restaurant? Set it up yourself in a minute.',
  'backOffice.create': 'Create restaurant',
  'backOffice.createCap': 'Your account already has a restaurant. Contact Romi to add another.',
  'backOffice.createTitle': 'Create your restaurant',
  'backOffice.createIntro':
    'You become the superuser of the new restaurant. Romi is told and will get in touch to help with the setup.',
  'settings.title': 'Restaurant settings',
  'settings.managerOnly': 'Only managers and the superuser can change restaurant settings.',
  'settings.general': 'Restaurant',
  'settings.colorHint': 'Used for buttons and highlights on your restaurant’s pages. Text stays readable whatever you pick.',
  'settings.languagesTitle': 'Languages',
  'settings.mainLanguageHint':
    'Menu items and texts must be filled in the main language. Secondary languages are optional extras.',
  'settings.mainChangeTitle': 'Changing the main language to {language}',
  'settings.mainChangeChecking': 'Checking which content already has {language} text…',
  'settings.mainChangeMissing':
    'Nothing is blocked. These items have no {language} text yet and will show another language until you add it:',
  'settings.mainChangeComplete': 'Everything already has {language} text.',
  'settings.missing.intro': 'Restaurant introduction',
  'settings.missing.menuCategory': 'Category: {name}',
  'settings.missing.menuItem': 'Menu item: {name}',
  'settings.missing.announcement': 'Announcement of branch: {name}',
  'settings.introTitle': 'Introduction',
  'settings.introHint':
    'Shown on your restaurant page. Visitors see their own language when it is filled, otherwise the main language.',
  'settings.introFor': 'Introduction ({language})',
  'settings.introMain': 'Main language',
  'settings.saved': 'Settings saved.',
  'accounts.title': 'Accounts',
  'accounts.managerOnly': 'Only managers and the superuser can manage accounts.',
  'accounts.roleFilter': 'Show role',
  'accounts.empty': 'No accounts with this role yet.',
  'accounts.pager': 'Account pages',
  'accounts.find': 'Find an account',
  'accounts.findIntro': 'Enter the exact phone number or email of a registered Romi account to grant it a role.',
  'accounts.search': 'Search',
  'accounts.noRole': 'No role in this restaurant',
  'accounts.role': 'Role',
  'accounts.branches': 'Branches',
  'accounts.branchesHint': 'The branches this person works in.',
  'accounts.restaurantWide': 'This role covers every branch.',
  'accounts.grantNotice': 'The person is told about the change.',
  'accounts.grant': 'Grant role',
  'accounts.saved': 'Role saved.',
  'accounts.edit': 'Edit',
  'accounts.revoke': 'Revoke',
  'accounts.confirmRevoke': 'Confirm revoke',
  'accounts.confirmRevokeOf': 'Confirm revoking the role of {name}',
  'accounts.revoked': 'Role revoked.',
  'staff.title': 'Staff',
  'staff.adminOnly': 'Only admins of this branch, managers, and the superuser manage its staff.',
  'staff.findIntro': 'Enter the exact phone number or email of a registered Romi account to add it as staff of this branch.',
  'staff.here': 'Works in this branch as staff.',
  'staff.alsoIn': 'Also in:',
  'staff.otherRole': 'This account holds the {role} role. Manage it on the restaurant accounts page.',
  'staff.add': 'Add to this branch',
  'staff.added': 'Added as staff of this branch.',
  'staff.remove': 'Remove',
  'staff.confirmRemove': 'Confirm remove',
  'staff.confirmRemoveOf': 'Confirm removing {name} from this branch',
  'staff.removed': 'Removed from this branch.',
  'staff.empty': 'No staff in this branch yet.',
  'staff.pager': 'Staff pages',
  'menu.title': 'Menu',
  'menu.managerOnly': 'Only managers and the superuser can edit the menu.',
  'menu.categoriesTitle': 'Categories',
  'menu.categoriesHint': 'Customers see the menu in this order. Items are listed under their category below.',
  'menu.noCategories': 'No categories yet. Add one to start the menu.',
  'menu.addCategory': 'Add category',
  'menu.editCategory': 'Edit category',
  'menu.nameFor': 'Name ({language})',
  'menu.nameMain': 'Main language, required',
  'menu.moveUp': 'Move {name} up',
  'menu.moveDown': 'Move {name} down',
  'menu.itemCount': '{count} item(s)',
  'menu.categorySaved': 'Category saved.',
  'menu.categoryDeleted': 'Category deleted.',
  'menu.confirmDeleteCategoryOf': 'Confirm deleting the category {name}',
  'menu.noItems': 'No items in this category yet.',
  'menu.addItem': 'Add item',
  'menu.editItem': 'Edit item',
  'menu.category': 'Category',
  'menu.price': 'Price',
  'menu.priceIn': 'In {currencies}.',
  'menu.priceWhole': 'Whole numbers only.',
  'menu.priceDecimals': 'Up to {decimals} decimals.',
  'menu.step': 'Quantity step',
  'menu.stepHint': 'How customers change the quantity: whole dishes, halves, or tenths for items sold by weight.',
  'menu.step.whole': 'Whole (1)',
  'menu.step.half': 'Allow half',
  'menu.step.tenth': 'Per kg',
  'menu.picture': 'Picture',
  'menu.pictureHint': 'Optional. Shown as a square. JPEG, PNG or WebP up to {max_mb} MB.',
  'menu.pictureOf': 'Picture of {name}',
  'menu.uploading': 'Uploading…',
  'menu.removePicture': 'Remove picture',
  'menu.itemSaved': 'Item saved.',
  'menu.itemDeleted': 'Item deleted.',
  'menu.confirmDeleteItemOf': 'Confirm deleting {name}',
  'branches.title': 'Branches',
  'branches.hint': 'Open a branch to work in its back office.',
  'branches.empty': 'No branch yet.',
  'branchMenu.title': 'Branch menu',
  'branchMenu.adminOnly': 'Only admins and above manage a branch menu.',
  'branchMenu.hint':
    'The restaurant menu as this branch serves it. Untick an item when it is sold out or not served here; set a daily stock to stop orders at zero.',
  'branchMenu.noMenu': 'The restaurant has no menu items yet.',
  'branchMenu.available': 'Available',
  'branchMenu.availableFor': 'Available: {name}',
  'branchMenu.unavailable': 'Unavailable',
  'branchMenu.soldOut': 'Sold out',
  'branchMenu.stockLeft': '{count} in stock',
  'branchMenu.basePrice': 'Base price {price}',
  'branchMenu.edit': 'Edit at this branch',
  'branchMenu.stock': 'Daily stock',
  'branchMenu.stockHint': 'Leave empty to not track stock. At 0 the item shows as sold out and cannot be ordered.',
  'branchMenu.price': 'Price at this branch',
  'branchMenu.priceHint': 'Leave empty to use the base price ({price}).',
  'branchMenu.priceManagerOnly': 'Only managers and the superuser change branch prices.',
  'branchMenu.saved': 'Saved.',
  'tables.title': 'Tables',
  'tables.adminOnly': 'Only admins and above manage tables.',
  'tables.hint':
    'Tables are for dine-in service by session: every order scanned at a table joins one bill until staff close it. If customers pay per order instead, skip tables — print the take-away QR and turn on the delivery-address field so they can type where the food goes.',
  'tables.qrTitle': 'QR codes',
  'tables.printQr.restaurant': 'Print restaurant QR',
  'tables.printQr.branch': 'Print branch QR',
  'tables.printQr.take_away': 'Print take-away QR',
  'tables.print': 'Print QR',
  'tables.printFor': 'Print QR: {name}',
  'tables.listTitle': 'Dine-in tables',
  'tables.empty': 'No tables yet.',
  'tables.name': 'Table name',
  'tables.nameHint': 'Up to {max} characters, unique in this branch.',
  'tables.add': 'Add table',
  'tables.editFor': 'Edit: {name}',
  'tables.confirmDeleteOf': 'Confirm deleting {name}',
  'tables.code': 'Code {code}',
  'tables.pager': 'Table pages',
  'tables.saved': 'Saved.',
  'tables.deleted': 'Table deleted.',
  'queue.title': 'Queue',
  'queue.filter': 'Statuses to show',
  'queue.shopOpen': 'Open',
  'queue.shopClosed': 'Closed',
  'queue.pin': 'Order PIN: {pin}',
  'queue.empty': 'No orders.',
  'queue.details': 'Details',
  'queue.next.sent': 'Confirm',
  'queue.next.confirmed': 'Start preparing',
  'queue.next.processing': 'Mark ready',
  'queue.next.ready': 'Complete',
  'queue.moved': 'This order is already “{status}”. Refreshing…',
  'orderDetails.code': 'Code {code}',
  'orderDetails.notFound': 'This order is not in this branch.',
  'orderDetails.print': 'Print',
  'orderDetails.edit': 'Edit',
  'orderDetails.done': 'Done editing',
  'orderDetails.tabs': 'Edit order',
  'orderDetails.tab.return': 'Return items',
  'orderDetails.tab.weight': 'Real weight',
  'orderDetails.returnHint': 'Returns come off the bill on any order, even a completed one.',
  'orderDetails.now': 'Now {quantity}',
  'orderDetails.after': 'After: {quantity}',
  'orderDetails.returnAmount': 'Return amount for {name}',
  'orderDetails.return': 'Return',
  'orderDetails.nothingLeft': 'Fully returned',
  'orderDetails.weightHint': 'Replaces the ordered amount with what the kitchen weighed.',
  'orderDetails.weight': 'Real weight of {name}',
  'orderDetails.saveWeight': 'Save weight',
  'orderDetails.noWeightItems': 'No items sold by weight in this order.',
  'orderDetails.weightNotActive': 'Real weight can only be entered while the order is active.',
  'orderDetails.staffCreated': 'Created by staff for a walk-in.',
  'orderDetails.staffCreatedFor': 'Created by staff for a customer account.',
  'createOrder.title': 'Create order',
  'createOrder.form': 'New order',
  'createOrder.type': 'Order type',
  'createOrder.dineIn': 'Dine-in',
  'createOrder.table': 'Table',
  'createOrder.chooseTable': 'Choose a table',
  'createOrder.noTables': 'No tables in this branch yet. Dine-in orders need one (admins add them under Tables).',
  'createOrder.forCustomer': 'Link a customer account',
  'createOrder.customerHint': 'Optional. The exact phone number or email of a registered Romi account; the order then shows in their history.',
  'createOrder.review': 'Review order',
  'createOrder.empty': 'Nothing added yet. Pick items from the menu above.',
  'createOrder.place': 'Place order',
  'createOrder.placed': 'Order #{number} placed.',
  'createOrder.viewOrder': 'View order',
  'createOrder.viewSession': 'View table {name}',
  'sessions.title': 'Sessions',
  'sessions.empty': 'No open sessions.',
  'sessions.orders': '{count} orders',
  'sessions.active': '{count} active',
  'sessions.notFound': 'This session is not in this branch.',
  'sessions.code': 'Code {code}',
  'sessions.close': 'Close session',
  'sessions.closeConfirm': 'Yes, close it',
  'sessions.closeKeep': 'Keep it open',
  'sessions.closeBlocked': '{count} active orders must be completed or canceled first.',
  'sessions.closedNotice': 'This session is closed.',
  'sessions.changeTable': 'Change table',
  'sessions.target': 'New table',
  'sessions.move': 'Move',
  'sessions.noOtherTables': 'No other tables in this branch.',
  'sessions.busy': 'Table {name} already has an open session. Merge the two parties into one bill?',
  'sessions.merge': 'Merge into table {name}',
  'sessions.closed': 'Closed',
  'search.title': 'Search',
  'search.tabs': 'What to search',
  'search.tab.orders': 'Orders',
  'search.tab.sessions': 'Sessions',
  'search.or': 'or',
  'search.orderCode': 'Order code',
  'search.sessionCode': 'Session code',
  'search.codeHint': 'The 10-character code on the customer’s order link.',
  'search.invalidCode': 'A code is exactly 10 letters and digits.',
  'search.number': 'Order number',
  'search.invalidNumber': 'Enter the daily order number (1, 2, 3…).',
  'search.date': 'Date',
  'search.dateRequired': 'Pick the date.',
  'search.time': 'Time',
  'search.table': 'Table',
  'search.tableRequired': 'Enter the table name.',
  'search.tableHint': 'Exactly as named, as on the ticket.',
  'search.byCode': 'Search by code',
  'search.byNumber': 'Search by number',
  'search.byTable': 'Search by table',
  'search.noOrder': 'No order matches.',
  'search.noSessions': 'No sessions match.',
  'stats.title': 'Statistics',
  'stats.adminOnly': 'Only admins and above see branch statistics.',
  'stats.tabs': 'Period',
  'stats.tab.date': 'Day',
  'stats.tab.month': 'Month',
  'stats.tab.year': 'Year',
  'stats.tab.range': 'Range',
  'stats.date': 'Date',
  'stats.month': 'Month',
  'stats.year': 'Year',
  'stats.from': 'From',
  'stats.to': 'To',
  'stats.span': '{from} – {to}',
  'stats.sales': 'Sales',
  'stats.salesHint': 'Every order except canceled and expired ones.',
  'stats.orders': '{count} order(s)',
  'stats.bestsellers': 'Bestsellers',
  'stats.allItems': 'All items',
  'stats.allItemsTitle': 'Items by sales',
  'stats.sold': 'Sold {quantity}',
  'stats.inOrders': 'in {count} order(s)',
  'stats.deletedItem': 'No longer on the menu',
  'stats.byStatus': 'Orders by status',
  'stats.ordersOfDate': 'All orders of this day',
  'stats.ordersOfDateTitle': 'Orders of {date}',
  'stats.noOrders': 'No orders that day.',
  'stats.orderPages': 'Order pages',
  'stats.byCategory': 'Categories',
  'stats.byType': 'Order type',
  'stats.type.dine_in': 'Dine-in',
  'stats.type.take_away': 'Take-away',
  'stats.count': 'Orders',
  'stats.quantity': 'Quantity',
  'stats.total': 'Total',
  'stats.nothingSold': 'Nothing sold in this period.',
  'stats.status': 'Status',
  'stats.allStatuses': 'All statuses',
  'branchSettings.title': 'Branch settings',
  'branchSettings.adminOnly': 'Only admins and above change branch settings.',
  'branchSettings.shopTitle': 'Shop',
  'branchSettings.shopHint':
    'Customers can only order while the shop is open. Open it when the day starts and close it when you stop taking orders.',
  'branchSettings.status.open': 'Open',
  'branchSettings.status.closed': 'Closed',
  'branchSettings.openShop': 'Open shop',
  'branchSettings.closeShop': 'Close shop',
  'branchSettings.openConfirm': 'Yes, open it',
  'branchSettings.closeConfirm': 'Yes, close it',
  'branchSettings.shopOpened': 'Shop opened.',
  'branchSettings.shopClosed': 'Shop closed.',
  'branchSettings.orderingTitle': 'Ordering',
  'branchSettings.mode': 'Ordering mode',
  'branchSettings.mode.open': 'Open — anyone can order without logging in',
  'branchSettings.mode.login': 'Login — customers must log in to order',
  'branchSettings.mode.closed': 'Closed — no customer ordering',
  'branchSettings.modeHint.open':
    'Orders from customers who are not logged in wait as Sent until you confirm them, and expire on their own if nobody does.',
  'branchSettings.modeHint.login':
    'Each customer may hold a limited number of active orders (confirmed, preparing or ready) at once.',
  'branchSettings.modeHint.closed':
    'Customers who scan a QR code are told the branch turned QR ordering off and are pointed to the branch page. Staff can still create orders.',
  'branchSettings.expiry': 'Expire unconfirmed orders after (minutes)',
  'branchSettings.expiryHint': 'Anti-spam: 1–{max} minutes. Default 10.',
  'branchSettings.cap': 'Active orders per customer',
  'branchSettings.capHint': 'Anti-spam: 1–{max}. Default 2.',
  'branchSettings.pinGate': 'Require a PIN to order',
  'branchSettings.pinGateHint':
    'For branches that only serve customers on the premises: tell them the PIN in person, and nobody elsewhere can order.',
  'branchSettings.pin': 'Order PIN',
  'branchSettings.pinHint': 'Up to {max} characters. Customers type it when placing an order.',
  'branchSettings.deliveryAddress': 'Ask take-away customers where to deliver',
  'branchSettings.deliveryAddressHint':
    'Adds a free-text field to take-away orders: an address, the counter, or a table name. Use it when customers share tables but pay per order; for one bill per table, create dine-in tables instead.',
  'branchSettings.saved': 'Settings saved.',
  'branchInfo.title': 'Branch info',
  'branchInfo.managerOnly': 'Only managers and the superuser edit branch info.',
  'branchInfo.hint': 'Shown on the public branch page and printed at the top of tickets.',
  'branchInfo.name': 'Branch name',
  'branchInfo.address': 'Address',
  'branchInfo.phone': 'Phone number',
  'branchInfo.phoneHint': 'Leave empty for no phone.',
  'branchInfo.openingHoursTitle': 'Opening hours',
  'branchInfo.openingHoursHint': 'Tick the days the branch opens and set the hours. Days are named in the visitor’s language.',
  'branchInfo.showOpeningHours': 'Show opening hours on the branch page',
  'openingHours.mode.same': 'Same every day',
  'openingHours.mode.weekday_weekend': 'Weekdays / weekend',
  'openingHours.mode.per_day': 'Per day',
  'openingHours.to': 'to',
  'openingHours.opens': 'opens',
  'openingHours.closes': 'closes',
  'openingHours.closed': 'closed',
  'day.mon': 'Monday',
  'day.tue': 'Tuesday',
  'day.wed': 'Wednesday',
  'day.thu': 'Thursday',
  'day.fri': 'Friday',
  'day.sat': 'Saturday',
  'day.sun': 'Sunday',
  'branchInfo.announcementTitle': 'Announcement',
  'branchInfo.announcementHint':
    'Shown on the branch page in the language the visitor reads. When only one language is filled, that one is shown to everyone.',
  'branchInfo.announcementFor': 'Announcement ({language})',
  'branchInfo.saved': 'Branch info saved.',
  'qr.title': 'Print QR code',
  'qr.print': 'Print',
  'qr.back': 'Back to tables',
  'qr.missing': 'Nothing to print here. Choose a QR code on the tables page.',
  'qr.image': 'QR code for {url}',
  'qr.sign.restaurant': 'Scan to visit us online',
  'qr.sign.branch': 'Scan for our menu and info',
  'qr.sign.take_away': 'Scan to order take-away',
  'qr.sign.table': 'Scan to order at this table',
  'order.takeAway': 'Take-away',
  'order.table': 'Table {name}',
  'order.menuEmpty': 'Nothing on the menu yet.',
  'order.categories': 'Categories',
  'order.notFound': 'This ordering link does not work. Check it or ask the staff.',
  'order.loginRequired': 'This branch asks customers to log in before ordering.',
  'order.shopClosed': 'This branch is closed right now and is not taking orders.',
  'order.qrOrderingOff': 'This branch has turned off ordering by QR code. Please order with the staff.',
  'order.branchPage': 'See the menu on the branch page',
  'order.loginToOrder': 'Log in to order',
  'order.add': 'Add',
  'order.addItem': 'Add {name}',
  'order.increase': 'More {name}',
  'order.decrease': 'Less {name}',
  'order.remove': 'Remove {name}',
  'order.removeShort': 'Remove',
  'order.quantityOf': 'Quantity of {name}',
  'order.enlarge': 'Enlarge picture of {name}',
  'order.close': 'Close',
  'order.viewCart': 'View order',
  'order.itemCount': '{count} item(s)',
  'order.cartTitle': 'Your order',
  'order.cartEmpty': 'Nothing here yet. Add something from the menu.',
  'order.lineUnavailable': 'No longer available. Remove it to continue.',
  'order.lineNoteAdd': 'Add a note',
  'order.lineNote': 'Note for {name}',
  'order.lineNotePlaceholder': 'Less spicy, no onions…',
  'order.note': 'Note for the whole order',
  'order.noteHint': 'Allergies, serve together, anything the staff should know. Up to {max} characters.',
  'order.deliveryAddress': 'Where should we bring it?',
  'order.deliveryAddressHint': 'An address, the counter, or your table name. Up to {max} characters.',
  'order.pin': 'Order PIN',
  'order.pinHint': 'Ask the staff for the PIN.',
  'order.expiryHint': 'Unconfirmed orders expire after {minutes} minutes.',
  'order.total': 'Total',
  'order.place': 'Place order',
  'myOrders.title': 'My orders',
  'myOrders.active': 'Active orders',
  'myOrders.past': 'Past orders',
  'myOrders.empty': 'No orders yet. Scan a table or take-away QR at a restaurant to place one.',
  'myOrders.pager': 'Order pages',
  'myOrders.atCounter': 'Placed at the counter',
  'myOrders.venueGone': 'This place is no longer on Romi.',
  'track.title': 'Order #{number}',
  'track.notFound': 'This order link does not work. Check it or ask the staff.',
  'track.sessionNotFound': 'This table link does not work. Check it or ask the staff.',
  'track.progress': 'Order progress',
  'track.status.sent': 'Sent',
  'track.status.confirmed': 'Confirmed',
  'track.status.processing': 'Preparing',
  'track.status.ready': 'Ready',
  'track.status.completed': 'Completed',
  'track.status.canceled': 'Canceled',
  'track.status.expired': 'Expired',
  'track.statusHint.sent': 'Waiting for the staff to confirm your order.',
  'track.statusHint.confirmed': 'The staff have your order.',
  'track.statusHint.processing': 'Your order is being prepared.',
  'track.statusHint.ready': 'Your order is ready.',
  'track.statusHint.completed': 'Done. Thank you!',
  'track.statusHint.canceled': 'This order was canceled.',
  'track.statusHint.expired': 'This order was not confirmed in time and expired. Place it again if you still want it.',
  'track.expiresIn': 'Expires in {time} unless the staff confirm it.',
  'track.expiring': 'Expiring…',
  'track.live': 'Live updates',
  'track.connecting': 'Connecting…',
  'track.updatesPaused': 'Live updates paused. Refreshing now and then.',
  'track.note': 'Note',
  'track.deliveryAddress': 'Deliver to',
  'track.total': 'Total',
  'track.column.item': 'Item',
  'track.column.quantity': 'Qty',
  'track.column.unitPrice': 'Unit price',
  'track.column.amount': 'Amount',
  'track.returned': 'Returned {quantity}',
  'track.cancel': 'Cancel order',
  'track.cancelConfirm': 'Yes, cancel it',
  'track.cancelKeep': 'Keep it',
  'track.loginToCancel': 'Log in to manage this order',
  'track.seeSession': 'See everything ordered at table {name}',
  'track.sessionTitle': 'Table {name}',
  'track.sessionOpenedAt': 'Opened at {time}',
  'track.sessionClosedAt': 'Closed at {time}',
  'track.sessionEmpty': 'Nothing ordered at this table yet.',
  'track.sessionOrders': 'Orders at this table',
  'track.sessionTotal': 'Table total',
  'track.mine': 'Yours',
  'landing.tagline': 'Restaurant order management',
  'landing.notFound': 'This page does not exist. Check the link.',
  'landing.whatTitle': 'What is Romi?',
  'landing.what':
    'Romi is a hosted restaurant order management service that brings your restaurant online in minutes. Guests scan a QR code to browse your menu and order directly from their phones, while your staff manage the kitchen queue, table sessions, and daily operations from one simple dashboard.',
  'landing.whyTitle': 'Why restaurants choose Romi',
  'landing.why1.title': 'Faster order placement',
  'landing.why1.body': 'Guests order the moment they are ready. No waiting to flag down staff.',
  'landing.why2.title': 'Fewer order mistakes',
  'landing.why2.body':
    'Orders go straight from the guest’s phone to the kitchen, cutting out handwriting and miscommunication.',
  'landing.why3.title': 'Less workload for staff',
  'landing.why3.body': 'Your team spends less time taking orders and more time delivering great service.',
  'landing.why4.title': 'Insight into your sales',
  'landing.why4.body': 'Built-in statistics help you make better decisions.',
  'landing.featuresTitle': 'Everything you need',
  'landing.feature1': 'Self-ordering via QR code',
  'landing.feature2': 'Real-time kitchen queue',
  'landing.feature3': 'Dine-in and take-away orders',
  'landing.feature4': 'Table session management',
  'landing.feature5': 'Easy menu and table setup',
  'landing.feature6': 'Sales statistics and reports',
  'landing.feature7': 'Multiple branches per restaurant',
  'landing.feature8': 'Vietnamese and English built in',
  'landing.feature9': 'Works on any phone, no app to install',
  'landing.howTitle': 'How it works',
  'landing.how1.title': 'Guest scans a QR code',
  'landing.how1.body':
    'Scan the QR code at the table to dine in, or the take-away QR code to order to go. Your menu opens instantly, no app to install.',
  'landing.how2.title': 'Browse and order',
  'landing.how2.body': 'Guests pick dishes and place their order right from their phone.',
  'landing.how3.title': 'Kitchen prepares',
  'landing.how3.body': 'Orders appear in the kitchen queue in real time, ready to be prepared.',
  'landing.how4.title': 'Track to completion',
  'landing.how4.body': 'Staff follow each order, dine-in or take-away, through the queue until it is served.',
  'landing.pricingTitle': 'Free until December 31, 2026',
  'landing.pricingExpected': 'Expected price after that:',
  'landing.pricingPrice': '100 VND per order',
  'landing.pricingEx1.title': '100 orders a day (about 3,000 orders a month)',
  'landing.pricingEx1.body': 'Only 10,000 VND a day (about 300,000 VND a month)',
  'landing.pricingEx2.title': '50 orders a day (about 1,500 orders a month)',
  'landing.pricingEx2.body': 'Only 5,000 VND a day (about 150,000 VND a month)',
  'landing.pricingReward': 'Join before December 31, 2026 and stay free through January 31, 2027.',
  'landing.pricingNote': 'The final price will be announced in early December 2026.',
  'landing.startFree': 'Start free',
  'landing.createRestaurant': 'Create restaurant',
  'landing.contactTitle': 'Bring Romi to your restaurant',
  'landing.contact':
    'Create your account and your restaurant yourself in a few minutes. Your team then manages its own menu, tables, and staff and admin accounts. Questions, or a second restaurant? Get in touch.',
  'guide.title': 'User guide',
  'guide.intro': 'How to set up and run a restaurant on Romi, step by step. Each guide prints as a PDF.',
  'guide.createRestaurant': 'Create a restaurant',
  'guide.setupRestaurant': 'Set up the restaurant',
  'guide.setupBranch': 'Set up a branch',
  'guide.branchOperation': 'Run a branch',
  'guide.print': 'Print / Save as PDF',
  'guide.updated': 'Last updated {date}',
  'landing.guide': 'How to get started',
  'landing.footer': '© {year} Romi · All rights reserved.',
  'landing.poweredBy': 'Powered by Romi',
  'landing.manageRestaurant': 'Manage restaurant',
  'landing.manageBranch': 'Manage branch',
  'landing.openQueue': 'Open the queue',
  'landing.branchesTitle': 'Branches',
  'landing.noBranches': 'No branch is open to the public yet.',
  'landing.allBranches': 'All branches of {name}',
  'landing.open': 'Open',
  'landing.closed': 'Closed',
  'landing.unavailable': 'This restaurant is temporarily unavailable.',
  'landing.announcementTitle': 'Announcement',
  'landing.infoTitle': 'Branch info',
  'landing.openingHours': 'Opening hours',
  'landing.address': 'Address',
  'landing.phone': 'Phone',
  'landing.menuTitle': 'Menu',
  'landing.menuHint': 'Prices as served at this branch.',
  'common.previous': 'Previous',
  'common.next': 'Next',
  'common.pageRange': '{from}–{to} of {total}',
  'common.optional': 'optional',
  'common.noMatch': 'No match.',
  'common.typeToSearch': 'Type to search',
  'common.save': 'Save',
  'common.cancel': 'Cancel',
  'common.loading': 'Loading…',
  'common.retry': 'Retry',
  'common.back': 'Back',
  'common.edit': 'Edit',
  'common.delete': 'Delete',
  'common.confirmDelete': 'Confirm delete',
}

export type MessageKey = keyof typeof en
export type Messages = Record<MessageKey, string>
